import { useRef, useState } from "react";
import { Loader2, RefreshCw, Table2, Upload } from "lucide-react";

import { cn } from "@/lib/utils";
import type { ManualProcessOutcome } from "@/types/domain";
import { useRetryExtraction } from "@/hooks/mutations/useRetryExtraction";
import { useUploadCorrectedFile } from "@/hooks/mutations/useUploadCorrectedFile";

// Espejo de MAX_UPLOAD_BYTES del backend: avisar acá evita subir de más para
// recibir un 413.
const MAX_UPLOAD_MB = 40;

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    // El dataURL viene como "data:application/pdf;base64,XXXX".
    reader.onload = () =>
      resolve(String(reader.result).split(",")[1] ?? "");
    reader.readAsDataURL(file);
  });
}

/**
 * Las tres salidas de un archivo que falló: reintentarlo tal cual con el modelo
 * bueno, releerlo como presupuesto de obra, o reemplazarlo por uno corregido (el
 * optimizado, o la relación de precios correcta si el scraper bajó otra). Si la
 * correcta viene partida en varios PDFs, el primero reemplaza y el resto se suma
 * como relaciones nuevas de la licitación, igual que las partidas de un zip.
 */
export function SolutionButtons({
  fileId,
  nomenclature,
  onResult,
}: {
  fileId: number;
  nomenclature: string;
  onResult: (result: ManualProcessOutcome) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const retry = useRetryExtraction();
  const upload = useUploadCorrectedFile();
  // `upload.isPending` se apaga entre un PDF y el siguiente; esto cubre la tanda.
  const [isUploading, setIsUploading] = useState(false);
  const isBusy = retry.isPending || isUploading;
  // Los dos reintentos comparten mutation, así que el spinner necesita saber
  // cuál de los dos botones la disparó.
  const [retryMode, setRetryMode] = useState<"budget" | undefined>();

  async function handleRetry(mode?: "budget") {
    // Leerlo como presupuesto obliga a la IA a no devolver 0 ítems, así que sobre
    // una relación de insumos normal inventaría partidas: se confirma antes.
    if (mode === "budget") {
      const confirmed = window.confirm(
        `Vas a releer el archivo de:\n\n${nomenclature}\n\n` +
          `como PRESUPUESTO DE OBRA (tabla de partidas con metrado), no como ` +
          `relación de insumos.\n\nUsalo solo si ya abriste el PDF y confirmaste ` +
          `que es un presupuesto: en este modo la IA tiene prohibido devolver ` +
          `cero ítems, así que sobre cualquier otro documento va a inventar.`,
      );
      if (!confirmed) return;
    }

    setRetryMode(mode);
    try {
      onResult(await retry.mutateAsync({ fileId, mode }));
    } catch (err: any) {
      onResult({ ok: false, itemCount: 0, message: errorText(err) });
    }
  }

  async function handleUpload(files: File[]) {
    const [replacement, ...additions] = files;
    if (!replacement) return;

    const oversized = files.find(
      (file) => file.size > MAX_UPLOAD_MB * 1024 * 1024,
    );
    if (oversized) {
      onResult({
        ok: false,
        itemCount: 0,
        message: `"${oversized.name}" pesa ${(oversized.size / 1048576).toFixed(1)} MB y el tope de subida es ${MAX_UPLOAD_MB} MB.`,
      });
      return;
    }

    // Reemplazar borra los ítems de la versión activa y, por cascade, los
    // intereses que un cliente haya marcado sobre ellos. No hay vuelta atrás.
    const confirmed = window.confirm(
      `Vas a reemplazar la relación de precios de:\n\n${nomenclature}\n\n` +
        `por "${replacement.name}".\n\n` +
        (additions.length > 0
          ? `Y vas a agregar como relaciones de precios nuevas de la misma licitación:\n` +
            `${additions.map((file) => `• ${file.name}`).join("\n")}\n\n`
          : "") +
        `Los ítems de la versión actual se borran, junto con los intereses que ` +
        `algún cliente haya marcado sobre ellos. No se puede deshacer.`,
    );
    if (!confirmed) return;

    setIsUploading(true);
    // En serie: cada PDF es una llamada a la IA, y juntos no entran en el body.
    const outcomes: ManualProcessOutcome[] = [];
    for (const [index, file] of files.entries()) {
      outcomes.push(await uploadFile(file, index > 0));
    }
    setIsUploading(false);

    onResult(
      outcomes.length === 1 ? outcomes[0]! : summarizeUploads(files, outcomes),
    );
  }

  async function uploadFile(
    file: File,
    asNewPriceRelation: boolean,
  ): Promise<ManualProcessOutcome> {
    try {
      return await upload.mutateAsync({
        fileId,
        fileName: file.name,
        base64Data: await readAsBase64(file),
        asNewPriceRelation,
      });
    } catch (err: any) {
      return { ok: false, itemCount: 0, message: errorText(err) };
    }
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        disabled={isBusy}
        onClick={() => handleRetry()}
        title="Reintentar con el modelo bueno y sin el tope de páginas"
        className={buttonClass}
      >
        {retryMode === undefined && retry.isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <RefreshCw className="size-4" />
        )}
      </button>

      <button
        type="button"
        disabled={isBusy}
        onClick={() => handleRetry("budget")}
        title="Releer el archivo como presupuesto de obra (tabla de partidas)"
        className={buttonClass}
      >
        {retryMode === "budget" && retry.isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Table2 className="size-4" />
        )}
      </button>

      <button
        type="button"
        disabled={isBusy}
        onClick={() => inputRef.current?.click()}
        title={`Subir la relación de precios corregida; si viene en varios PDFs, selecciónalos todos (máx. ${MAX_UPLOAD_MB} MB c/u)`}
        className={buttonClass}
      >
        {isUploading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Upload className="size-4" />
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          // Limpiar el input permite reintentar con el MISMO archivo.
          e.target.value = "";
          handleUpload(files);
        }}
      />
    </div>
  );
}

const buttonClass =
  "inline-flex size-8 cursor-pointer items-center justify-center rounded-full border bg-slate-50 text-slate-600 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";

// fetchToBackend lanza el body crudo ({ error } | { message }), no un Error.
function errorText(err: any): string {
  return err?.error || err?.message || "Falló la petición";
}

/** Junta en un solo resultado de fila lo que pasó con cada PDF de la tanda. */
function summarizeUploads(
  files: File[],
  outcomes: ManualProcessOutcome[],
): ManualProcessOutcome {
  const okCount = outcomes.filter((outcome) => outcome.ok).length;
  const fileLines = outcomes.map(
    (outcome, index) =>
      `${outcome.ok ? "✓" : "✗"} ${files[index]!.name}: ${outcome.message}`,
  );
  return {
    ok: okCount === outcomes.length,
    itemCount: outcomes.reduce((total, outcome) => total + outcome.itemCount, 0),
    message: [
      `${okCount} de ${outcomes.length} archivos procesados.`,
      ...fileLines,
    ].join("\n"),
  };
}

/** El resultado del último reproceso hecho desde esta pantalla. */
export function SolutionResult({
  result,
}: {
  result: ManualProcessOutcome | undefined;
}) {
  if (!result) return <span className="text-sm text-muted-foreground">—</span>;

  return (
    <span
      className={cn(
        // Sin recorte: con varios PDFs hay una línea por archivo y todas importan.
        "whitespace-pre-line text-sm",
        result.ok
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-destructive",
      )}
    >
      {result.ok ? "✓ " : "✗ "}
      {result.message}
    </span>
  );
}

/** Estado local de los resultados, uno por archivo. */
export function useSolutionResults() {
  const [results, setResults] = useState<
    Record<number, ManualProcessOutcome>
  >({});
  return {
    results,
    saveResult: (fileId: number, result: ManualProcessOutcome) =>
      setResults((prev) => ({ ...prev, [fileId]: result })),
  };
}
